import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { CSSTransition } from 'react-transition-group';
import { srConfig } from '@config';
import { KEY_CODES } from '@utils';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges;

  const [activeTabId, setActiveTabId] = useState(0);
  const [tabFocus, setTabFocus] = useState(null);
  const tabs = useRef([]);
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealContainer.current, srConfig());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const focusTab = () => {
    if (tabs.current[tabFocus]) {
      tabs.current[tabFocus].focus();
      return;
    }
    if (tabFocus >= tabs.current.length) {
      setTabFocus(0);
    }
    if (tabFocus < 0) {
      setTabFocus(tabs.current.length - 1);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => focusTab(), [tabFocus]);

  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ARROW_UP:
        e.preventDefault();
        setTabFocus(tabFocus - 1);
        break;
      case KEY_CODES.ARROW_DOWN:
        e.preventDefault();
        setTabFocus(tabFocus + 1);
        break;
      default:
        break;
    }
  };

  return (
    <section id="jobs" ref={revealContainer}>
      <h2 className="numbered-heading">Where I&apos;ve Worked</h2>

      <div className="inner flex max-w-[700px] max-[600px]:block min-[700px]:min-h-[340px]">

        {/* Tab list — tab-list and tab-button classes defined in global.css */}
        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
        <div
          className="tab-list"
          role="tablist"
          aria-label="Job tabs"
          onKeyDown={e => onKeyDown(e)}>

          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { company } = node.frontmatter;
              return (
                <button
                  key={i}
                  className={`tab-button${activeTabId === i ? ' is-active' : ''}`}
                  onClick={() => setActiveTabId(i)}
                  ref={el => (tabs.current[i] = el)}
                  id={`tab-${i}`}
                  role="tab"
                  tabIndex={activeTabId === i ? '0' : '-1'}
                  aria-selected={activeTabId === i}
                  aria-controls={`panel-${i}`}>
                  <span>{company}</span>
                </button>
              );
            })}

          {/* tab-highlight: transform driven by --active-tab CSS custom property */}
          <div
            className="tab-highlight"
            style={{ '--active-tab': activeTabId }}
            aria-hidden="true"
          />
        </div>

        {/* Tab panels */}
        <div className="relative w-full ml-5 max-[600px]:ml-0">
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { frontmatter, html } = node;
              const { title, url, company, range } = frontmatter;

              return (
                <CSSTransition key={i} in={activeTabId === i} timeout={250} classNames="fade">
                  {/* tab-panel ul styles defined in global.css */}
                  <div
                    className="tab-panel w-full h-auto p-[10px_5px]"
                    id={`panel-${i}`}
                    role="tabpanel"
                    tabIndex={activeTabId === i ? '0' : '-1'}
                    aria-labelledby={`tab-${i}`}
                    aria-hidden={activeTabId !== i}
                    hidden={activeTabId !== i}>

                    <h3 className="mb-0.5 text-[var(--fz-xxl)] font-medium leading-[1.3]">
                      <span>{title}</span>
                      <span className="text-accent">
                        &nbsp;@&nbsp;
                        <a href={url} className="inline-link text-accent">
                          {company}
                        </a>
                      </span>
                    </h3>

                    <p className="mb-[25px] text-light-slate font-mono text-[var(--fz-xs)]">
                      {range}
                    </p>

                    <div dangerouslySetInnerHTML={{ __html: html }} />
                  </div>
                </CSSTransition>
              );
            })}
        </div>
      </div>
    </section>
  );
};

export default Jobs;
